import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
	AlertCircle,
	Boxes,
	ChevronDown,
	ChevronRight,
	Columns3,
	Factory,
	Filter as FilterIcon,
	FolderTree,
	HelpCircle,
	LayoutTemplate,
	MoreVertical,
	Newspaper,
	Pencil,
	Plus,
	RotateCcw,
	Search,
	Tags,
	Trash2
} from 'lucide-react';
import { CustomDeleteBehavior, type CategoryToken } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { Pill } from '~/components/pill';
import { serverMessage } from '~/lib/api/server-error';
import { useCategoriesList, useCategoryMutations, useProductSortTypes } from '~/hooks/use-categories';
import { useFilterTemplatesList } from '~/hooks/use-manufacturers';
import { buildCategoryTree, type CategoryTreeNode } from '~/lib/schemas/category';
import { CategoryRelationsModal, type RelationKind } from './relation-modals';

/* ----------------------------------------------------------------- column model */

interface ColumnDef {
	key: string;
	/** i18n key under `catalog.categories.columns`. */
	label: string;
	defaultVisible: boolean;
	align: 'left' | 'center' | 'right';
	width: string;
	render: (node: CategoryTreeNode, ctx: RenderCtx) => ReactNode;
}

interface RenderCtx {
	sortTypeName: Map<number, string | undefined>;
	templateName: Map<number | undefined, string | undefined>;
	categoryName: Map<number, string | undefined>;
	t: (k: string, o?: Record<string, unknown>) => string;
}

const BoolCell = ({ on, title }: { on?: boolean; title: string }) =>
	on ? (
		<span className="text-success-fg" title={title}>
			✓
		</span>
	) : (
		<span className="text-foreground-disabled">—</span>
	);

const COLUMNS: ColumnDef[] = [
	{
		key: 'dynamic',
		label: 'dynamic',
		defaultVisible: true,
		align: 'center',
		width: '90px',
		render: (n, { t }) =>
			n.IsDynamic ? (
				<Pill variant="info" icon={FilterIcon}>
					{t('catalog.categories.dynamic')}
				</Pill>
			) : (
				<span className="text-foreground-disabled">—</span>
			)
	},
	{
		key: 'productSort',
		label: 'productSort',
		defaultVisible: true,
		align: 'left',
		width: '150px',
		render: (n, c) => <span className="text-foreground-muted">{c.sortTypeName.get(n.pst as number) ?? ''}</span>
	},
	{
		key: 'filterTemplate',
		label: 'filterTemplate',
		defaultVisible: true,
		align: 'left',
		width: '150px',
		render: (n, c) => <span className="text-foreground-muted">{c.templateName.get(n.FilterTemplateId ?? undefined) ?? ''}</span>
	},
	{
		key: 'color',
		label: 'color',
		defaultVisible: true,
		align: 'center',
		width: '70px',
		render: n =>
			n.chex ? (
				<span
					className="inline-block h-3.5 w-3.5 rounded-full border border-border align-middle"
					style={{ backgroundColor: n.chex }}
					title={n.chex}
				/>
			) : (
				<span className="text-foreground-disabled">—</span>
			)
	},
	{
		key: 'productCount',
		label: 'productCount',
		defaultVisible: true,
		align: 'right',
		width: '90px',
		render: n => <span className="tabular-nums text-foreground-muted">{n.product_count ?? 0}</span>
	},
	{
		key: 'visible',
		label: 'visible',
		defaultVisible: true,
		align: 'center',
		width: '80px',
		render: (n, { t }) => <BoolCell on={n.iv} title={t('catalog.categories.columns.visible')} />
	},
	{
		key: 'sort',
		label: 'sort',
		defaultVisible: true,
		align: 'right',
		width: '70px',
		render: n => <span className="tabular-nums text-foreground-muted">{n.si ?? 0}</span>
	},
	{
		key: 'code',
		label: 'code',
		defaultVisible: false,
		align: 'left',
		width: '120px',
		render: n => <span className="text-foreground-muted">{n.cd}</span>
	},
	{
		key: 'erpCode',
		label: 'erpCode',
		defaultVisible: false,
		align: 'left',
		width: '120px',
		render: n => <span className="text-foreground-muted">{n.erpcd}</span>
	},
	{
		key: 'categoryWizard',
		label: 'categoryWizard',
		defaultVisible: false,
		align: 'left',
		width: '150px',
		render: (n, c) => (
			<span className="text-foreground-muted">{n.categoryWizardId != null ? c.categoryName.get(n.categoryWizardId) : ''}</span>
		)
	},
	{
		key: 'walkTree',
		label: 'walkTree',
		defaultVisible: false,
		align: 'center',
		width: '90px',
		render: (n, { t }) => <BoolCell on={n.wt} title={t('catalog.categories.columns.walkTree')} />
	},
	{
		key: 'showZeroQty',
		label: 'showZeroQty',
		defaultVisible: false,
		align: 'center',
		width: '90px',
		render: (n, { t }) => <BoolCell on={n.szq} title={t('catalog.categories.columns.showZeroQty')} />
	},
	{
		key: 'landingPage',
		label: 'landingPage',
		defaultVisible: false,
		align: 'center',
		width: '90px',
		render: (n, { t }) => <BoolCell on={n.ilp} title={t('catalog.categories.columns.landingPage')} />
	},
	{
		key: 'topPage',
		label: 'topPage',
		defaultVisible: false,
		align: 'center',
		width: '80px',
		render: (n, { t }) => <BoolCell on={n.itp} title={t('catalog.categories.columns.topPage')} />
	},
	{
		key: 'excludeSubcategory',
		label: 'excludeSubcategory',
		defaultVisible: false,
		align: 'center',
		width: '110px',
		render: (n, { t }) => <BoolCell on={n.hidden_sbc} title={t('catalog.categories.columns.excludeSubcategory')} />
	},
	{
		key: 'excludeRelated',
		label: 'excludeRelated',
		defaultVisible: false,
		align: 'center',
		width: '110px',
		render: (n, { t }) => <BoolCell on={n.exclude_rel_category} title={t('catalog.categories.columns.excludeRelated')} />
	},
	{
		key: 'frontExclusion',
		label: 'frontExclusion',
		defaultVisible: false,
		align: 'center',
		width: '100px',
		render: (n, { t }) => <BoolCell on={n.frontExclusion} title={t('catalog.categories.columns.frontExclusion')} />
	},
	{
		key: 'highlighted',
		label: 'highlighted',
		defaultVisible: false,
		align: 'center',
		width: '90px',
		render: (n, { t }) => <BoolCell on={n.isHighlighted} title={t('catalog.categories.columns.highlighted')} />
	}
];

const COL_STORAGE_KEY = 'admin-v2:categories:columns';

function loadColumnVisibility(): Record<string, boolean> {
	const base = Object.fromEntries(COLUMNS.map(c => [c.key, c.defaultVisible]));
	try {
		const raw = globalThis.localStorage?.getItem(COL_STORAGE_KEY);
		if (raw) {
			const parsed = JSON.parse(raw) as Record<string, boolean>;
			for (const c of COLUMNS) if (typeof parsed[c.key] === 'boolean') base[c.key] = parsed[c.key];
		}
	} catch {
		/* storage blocked → defaults */
	}
	return base;
}

/* ----------------------------------------------------------------- search helpers (unchanged behaviour) */

function collectMatchIds(roots: CategoryTreeNode[], query: string): { matches: Set<number>; expand: Set<number> } {
	const matches = new Set<number>();
	const expand = new Set<number>();
	const walk = (node: CategoryTreeNode, ancestors: number[]): boolean => {
		const selfMatch = (node.nm ?? '').toLowerCase().includes(query);
		let childMatch = false;
		for (const child of node.children) childMatch = walk(child, [...ancestors, node.id!]) || childMatch;
		if (selfMatch || childMatch) {
			if (node.id != null) matches.add(node.id);
			if (childMatch) for (const a of ancestors.concat(node.id!)) expand.add(a);
		}
		return selfMatch || childMatch;
	};
	for (const r of roots) walk(r, []);
	return { matches, expand };
}

function flattenVisible(roots: CategoryTreeNode[], expanded: Set<number>, matches: Set<number> | null): CategoryTreeNode[] {
	const out: CategoryTreeNode[] = [];
	const walk = (nodes: CategoryTreeNode[]) => {
		for (const node of nodes) {
			if (matches && node.id != null && !matches.has(node.id)) continue;
			out.push(node);
			if (node.children.length && expanded.has(node.id!)) walk(node.children);
		}
	};
	walk(roots);
	return out;
}

/* ----------------------------------------------------------------- row actions dropdown */

const stickyLeft = 'sticky left-0 z-[1] border-r border-border bg-surface group-hover:bg-muted/50';
const stickyRight = 'sticky right-0 z-[1] border-l border-border bg-surface group-hover:bg-muted/50';
const stickyLeftHeader = 'sticky left-0 z-[2] border-r border-border bg-elevated';
const stickyRightHeader = 'sticky right-0 z-[2] border-l border-border bg-elevated';

function RowActions({ onEdit, onRelation, onDelete }: { onEdit: () => void; onRelation: (r: RelationKind) => void; onDelete: () => void }) {
	const { t } = useTranslation();
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="icon" className="h-7 w-7" aria-label={t('common.sections')}>
					<MoreVertical size={15} />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-52">
				<DropdownMenuItem onSelect={onEdit}>
					<Pencil size={14} /> {t('common.edit')}
				</DropdownMenuItem>
				<DropdownMenuSeparator />
				<DropdownMenuLabel>{t('catalog.categories.relations.manage')}</DropdownMenuLabel>
				<DropdownMenuItem onSelect={() => onRelation('products')}>
					<Boxes size={14} /> {t('catalog.categories.relations.products')}
				</DropdownMenuItem>
				<DropdownMenuItem onSelect={() => onRelation('manufacturers')}>
					<Factory size={14} /> {t('catalog.categories.relations.manufacturers')}
				</DropdownMenuItem>
				<DropdownMenuItem onSelect={() => onRelation('news')}>
					<Newspaper size={14} /> {t('catalog.categories.relations.news')}
				</DropdownMenuItem>
				<DropdownMenuItem onSelect={() => onRelation('attributes')}>
					<Tags size={14} /> {t('catalog.categories.relations.attributes')}
				</DropdownMenuItem>
				<DropdownMenuItem disabled>
					<HelpCircle size={14} /> {t('catalog.categories.relations.faq')} · {t('common.soon')}
				</DropdownMenuItem>
				<DropdownMenuItem disabled>
					<LayoutTemplate size={14} /> {t('catalog.categories.relations.themeTemplates')} · {t('common.soon')}
				</DropdownMenuItem>
				<DropdownMenuSeparator />
				<DropdownMenuItem variant="destructive" onSelect={onDelete}>
					<Trash2 size={14} /> {t('common.delete')}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

/* ----------------------------------------------------------------- main view */

export function CategoriesTreeView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = useCategoriesList();
	const { data: sortTypes } = useProductSortTypes();
	const { data: filterTemplates } = useFilterTemplatesList();
	const { remove } = useCategoryMutations();

	const [search, setSearch] = useState('');
	const [expanded, setExpanded] = useState<Set<number>>(new Set());
	const [deleteNode, setDeleteNode] = useState<CategoryTreeNode | null>(null);
	const [colVisible, setColVisible] = useState<Record<string, boolean>>(loadColumnVisibility);
	const [relation, setRelation] = useState<{ node: CategoryToken; kind: RelationKind } | null>(null);

	const roots = useMemo(() => buildCategoryTree(data ?? []), [data]);
	const query = search.trim().toLowerCase();
	const { matches, autoExpand } = useMemo(() => {
		if (!query) return { matches: null as Set<number> | null, autoExpand: null as Set<number> | null };
		const res = collectMatchIds(roots, query);
		return { matches: res.matches, autoExpand: res.expand };
	}, [roots, query]);

	const ctx = useMemo<RenderCtx>(
		() => ({
			sortTypeName: new Map((sortTypes ?? []).map(s => [s.id, s.nm])),
			templateName: new Map((filterTemplates ?? []).map(ft => [ft.id, ft.nm])),
			categoryName: new Map((data ?? []).filter(c => c.id != null).map(c => [c.id as number, c.nm])),
			t
		}),
		[sortTypes, filterTemplates, data, t]
	);

	const effectiveExpanded = query && autoExpand ? autoExpand : expanded;
	const rows = useMemo(() => flattenVisible(roots, effectiveExpanded, matches), [roots, effectiveExpanded, matches]);
	const cols = useMemo(() => COLUMNS.filter(c => colVisible[c.key] !== false), [colVisible]);

	const toggle = (id: number) =>
		setExpanded(prev => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	const toggleColumn = useCallback((key: string) => {
		setColVisible(prev => {
			const next = { ...prev, [key]: prev[key] === false };
			try {
				globalThis.localStorage?.setItem(COL_STORAGE_KEY, JSON.stringify(next));
			} catch {
				/* ignore */
			}
			return next;
		});
	}, []);

	const resetColumns = useCallback(() => {
		const next = Object.fromEntries(COLUMNS.map(c => [c.key, c.defaultVisible]));
		try {
			globalThis.localStorage?.setItem(COL_STORAGE_KEY, JSON.stringify(next));
		} catch {
			/* ignore */
		}
		setColVisible(next);
	}, []);

	const confirmDelete = (behavior: CustomDeleteBehavior) => {
		if (!deleteNode?.id) return;
		remove.mutate(
			{ id: deleteNode.id, behavior },
			{
				onSuccess: () => {
					toast.success(t('common.delete'));
					setDeleteNode(null);
				},
				onError: err => toast.error(serverMessage(err))
			}
		);
	};

	const total = data?.length ?? 0;
	const hasChildren = (deleteNode?.children.length ?? 0) > 0;
	const alignCls = (a: ColumnDef['align']) => (a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left');

	return (
		<div className="flex flex-col gap-3">
			{/* Page head */}
			<div className="flex items-start justify-between gap-4 border-b border-border pb-4">
				<div>
					<h1 className="text-2xl font-semibold tracking-[-0.015em] text-foreground">{t('catalog.categories.title')}</h1>
					<p className="mt-1 text-sm text-foreground-muted">{t('catalog.categories.subtitle', { count: total })}</p>
				</div>
				<Button onClick={() => navigate('/products/categories/create')}>
					<Plus size={16} /> {t('catalog.categories.add')}
				</Button>
			</div>

			{/* Toolbar: search + column selector */}
			<div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-3">
				<div className="relative min-w-60 flex-1 md:max-w-md">
					<Search size={16} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-foreground-subtle" />
					<Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('common.search')} className="pl-8" />
				</div>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="outline">
							<Columns3 size={16} /> {t('system.grid.columns')}
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="max-h-96 w-56 overflow-auto">
						<DropdownMenuLabel>{t('system.grid.columnsTitle')}</DropdownMenuLabel>
						<DropdownMenuSeparator />
						{COLUMNS.map(c => (
							<DropdownMenuCheckboxItem
								key={c.key}
								checked={colVisible[c.key] !== false}
								onSelect={e => e.preventDefault()}
								onCheckedChange={() => toggleColumn(c.key)}>
								{t(`catalog.categories.columns.${c.label}`)}
							</DropdownMenuCheckboxItem>
						))}
						<DropdownMenuSeparator />
						<DropdownMenuItem onSelect={resetColumns}>
							<RotateCcw size={14} /> {t('system.grid.columnsReset')}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			{/* Tree table */}
			<div className="overflow-x-auto rounded-md border border-border bg-surface">
				{isError ? (
					<div className="flex flex-col items-center gap-3 py-16 text-center">
						<AlertCircle size={40} className="text-danger-fg" strokeWidth={1.25} />
						<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
						<Button variant="outline" onClick={() => void refetch()}>
							{t('common.retry')}
						</Button>
					</div>
				) : isLoading ? (
					<div className="flex flex-col gap-2 p-3">
						{Array.from({ length: 8 }).map((_, i) => (
							<Skeleton key={i} className="h-10 w-full rounded-md" />
						))}
					</div>
				) : rows.length === 0 ? (
					<div className="flex flex-col items-center gap-3 py-16 text-center">
						<FolderTree size={36} className="text-foreground-disabled" strokeWidth={1.25} />
						<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
					</div>
				) : (
					<table className="w-full border-collapse text-sm">
						<thead className="bg-elevated text-xs text-foreground-muted">
							<tr className="border-b border-border">
								<th className={cn('px-3 py-2 text-left font-medium', stickyLeftHeader)} style={{ minWidth: 260 }}>
									{t('catalog.categories.columns.name')}
								</th>
								{cols.map(c => (
									<th key={c.key} className={cn('px-3 py-2 font-medium whitespace-nowrap', alignCls(c.align))} style={{ width: c.width }}>
										{t(`catalog.categories.columns.${c.label}`)}
									</th>
								))}
								<th className={cn('px-2 py-2', stickyRightHeader)} style={{ width: 48 }} />
							</tr>
						</thead>
						<tbody>
							{rows.map(node => {
								const expandable = node.children.length > 0;
								const isOpen = effectiveExpanded.has(node.id!);
								return (
									<tr key={node.id} className="group border-b border-border/60 hover:bg-muted/50">
										<td className={cn('px-3 py-2', stickyLeft)} style={{ minWidth: 260 }}>
											<div className="flex items-center gap-1.5" style={{ paddingLeft: node.depth * 20 }}>
												{expandable ? (
													<button
														type="button"
														onClick={() => toggle(node.id!)}
														className="rounded-sm p-0.5 text-foreground-subtle hover:bg-muted hover:text-foreground"
														aria-label={isOpen ? t('common.collapse') : t('common.expand')}>
														{isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
													</button>
												) : (
													<span className="w-[22px]" />
												)}
												<button
													type="button"
													onClick={() => navigate(`/products/categories/${node.id}`)}
													className="truncate text-left text-sm font-medium text-foreground hover:underline">
													{node.nm}
												</button>
											</div>
										</td>
										{cols.map(c => (
											<td key={c.key} className={cn('px-3 py-2 whitespace-nowrap', alignCls(c.align))}>
												{c.render(node, ctx)}
											</td>
										))}
										<td className={cn('px-2 py-1 text-center', stickyRight)}>
											<RowActions
												onEdit={() => navigate(`/products/categories/${node.id}`)}
												onRelation={kind => setRelation({ node, kind })}
												onDelete={() => setDeleteNode(node)}
											/>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				)}
			</div>

			{/* Relation managers */}
			<CategoryRelationsModal category={relation?.node ?? null} relation={relation?.kind ?? null} onClose={() => setRelation(null)} />

			{/* Delete dialog */}
			<Dialog open={Boolean(deleteNode)} onOpenChange={open => !open && setDeleteNode(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.categories.deleteTitle')}</DialogTitle>
						<DialogDescription>
							{hasChildren
								? t('catalog.categories.deleteWithChildrenBody', { name: deleteNode?.nm ?? '' })
								: t('catalog.categories.deleteBody', { name: deleteNode?.nm ?? '' })}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteNode(null)} disabled={remove.isPending}>
							{t('common.cancel')}
						</Button>
						{hasChildren && (
							<Button variant="outline" onClick={() => confirmDelete(CustomDeleteBehavior.KeepSubItems)} disabled={remove.isPending}>
								{t('catalog.categories.deleteKeepChildren')}
							</Button>
						)}
						<Button variant="destructive" onClick={() => confirmDelete(CustomDeleteBehavior.RemoveSubItems)} disabled={remove.isPending}>
							{hasChildren ? t('catalog.categories.deleteAll') : t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
